import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  MarkerType,
  type Node,
  type Edge,
  type NodeProps,
  type EdgeTypes,
  type NodeTypes,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import dagre from '@dagrejs/dagre'
import {
  Sparkles, Target, CheckSquare, Brain, Wrench, Repeat,
  AlertTriangle, User, Lightbulb, Network, Maximize2,
  Eye, EyeOff, Lock, MapPin,
} from 'lucide-react'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'
import { JourneyRail } from './JourneyRail'
import {
  hierarchyGraphService,
  type DreamGraphPayload,
  type GraphNode,
  type GraphEdge,
} from '../../services/hierarchyGraphService'
import { buildGraphModel, filterPayloadByKind } from '../lifemap/graphModel'

export type DreamGraphMode = 'branch' | 'full'

interface DreamGraphViewProps {
  /** Required in `branch` mode; ignored in `full` mode. */
  nodeId?: number
  /** Required in `branch` mode; ignored in `full` mode. */
  nodeType?: string
  /** `branch` (default): current subtree. `full`: every dream in the life map. */
  mode?: DreamGraphMode
  /** If true, render with a shorter canvas (for embedded tab / preview). */
  compact?: boolean
  /** If true, show an "Open full view" button that routes to /life-map. */
  showFullViewButton?: boolean
  /** If true, stretch to fill parent height (used by /life-map full-screen page). */
  fillHeight?: boolean
  /** In-place kind filter (Life Map hub, E12): a node kind
   *  ('dream'|'goal'|'habit'|'skill'|'task'), 'world' (people + places),
   *  or null/undefined for everything. Matched nodes keep their ancestor
   *  chain so the tree stays readable, never a floating orphan row. */
  kindFilter?: string | null
}

const TYPE_ICONS: Record<string, React.ReactNode> = {
  dream: <Sparkles className="w-3.5 h-3.5" />,
  goal: <Target className="w-3.5 h-3.5" />,
  task: <CheckSquare className="w-3.5 h-3.5" />,
  skill: <Brain className="w-3.5 h-3.5" />,
  habit: <Repeat className="w-3.5 h-3.5" />,
  roadblock: <AlertTriangle className="w-3.5 h-3.5" />,
  action_item: <Wrench className="w-3.5 h-3.5" />,
  person: <User className="w-3.5 h-3.5" />,
  aspiration: <Lightbulb className="w-3.5 h-3.5" />,
  place: <MapPin className="w-3.5 h-3.5" />,
}

const TYPE_ROUTES: Record<string, string> = {
  dream: '/dreams',
  goal: '/goals',
  task: '/tasks',
  skill: '/skills',
  habit: '/habits',
  person: '/my-people/person',
  place: '/places',
}

// Brand-purple palette keyed on node type. We express each style with inline
// CSS variables so the colors always track light/dark themes from index.css.
type NodeStyle = { borderColor: string; background: string }

function styleForNode(type: string): NodeStyle {
  switch (type) {
    case 'dream':
      return {
        borderColor: 'var(--brand-dark, #4E2F78)',
        background: 'color-mix(in srgb, var(--brand-dark) 12%, var(--bg-surface))',
      }
    case 'aspiration':
      return {
        borderColor: 'var(--brand-dark-mid, #5F3D92)',
        background: 'color-mix(in srgb, var(--brand-dark-mid) 10%, var(--bg-surface))',
      }
    case 'goal':
      return {
        borderColor: 'var(--brand-primary, #7851A9)',
        background: 'var(--brand-subtle, color-mix(in srgb, var(--brand-primary) 10%, var(--bg-surface)))',
      }
    case 'skill':
      return {
        borderColor: 'var(--brand-light, #9575C4)',
        background: 'color-mix(in srgb, var(--brand-light) 12%, var(--bg-surface))',
      }
    case 'habit':
      return {
        borderColor: 'var(--brand-light, #9575C4)',
        background: 'color-mix(in srgb, var(--brand-light) 8%, var(--bg-surface))',
      }
    case 'task':
      return {
        borderColor: 'var(--brand-lighter, #C9B8E8)',
        background: 'color-mix(in srgb, var(--brand-lighter) 20%, var(--bg-surface))',
      }
    case 'action_item':
      return {
        borderColor: 'var(--brand-lighter, #C9B8E8)',
        background: 'color-mix(in srgb, var(--brand-lighter) 12%, var(--bg-surface))',
      }
    case 'roadblock':
      return {
        borderColor: 'var(--semantic-error, #B75A4A)',
        background: 'color-mix(in srgb, var(--semantic-error) 12%, var(--bg-surface))',
      }
    case 'person':
      return {
        borderColor: 'var(--border-strong, #CFC6DD)',
        background: 'var(--bg-surface-alt, #EDE9F4)',
      }
    case 'place':
      // Distinct from the purple hierarchy palette — places are "where", a
      // different axis of the world.
      return {
        borderColor: 'var(--semantic-info, #4A90A4)',
        background: 'color-mix(in srgb, var(--semantic-info, #4A90A4) 12%, var(--bg-surface))',
      }
    default:
      return {
        borderColor: 'var(--border-strong, #CFC6DD)',
        background: 'var(--bg-surface, #FBFAFD)',
      }
  }
}

const NODE_WIDTH = 200
const NODE_HEIGHT = 56

type GraphNodeData = {
  node: GraphNode
  label: string
  /** Structural parent as resolved in this graph (for "adopted" subtitle logic). */
  structuralParent?: { id: number; type: string } | null
}

// ---------------------------------------------------------------------------
// Custom node renderer
// ---------------------------------------------------------------------------

function HierarchyNode({ data }: NodeProps<Node<GraphNodeData>>) {
  const { node, structuralParent } = data
  const icon = TYPE_ICONS[node.type] ?? <Network className="w-3.5 h-3.5" />
  const palette = styleForNode(node.type)
  const isCurrent = !!node.is_current
  const isGhost = !!node.isGhost
  const title = (node.title && node.title.trim()) || '(untitled)'
  const blockerCount = node.active_blocker_count ?? 0

  // Subtitle shows "also linked to" when the node's primary owner differs
  // from the structural parent rendered here.  Skip when the primary parent
  // equals the structural parent (avoids noisy labels on canonical children).
  const pp = node.primary_parent
  const showAdoptedSubtitle = !!(
    pp &&
    (!structuralParent ||
      pp.id !== structuralParent.id ||
      pp.type !== structuralParent.type) &&
    // And skip the self-referential case (should not happen, but be safe).
    !(pp.id === node.id && pp.type === node.type)
  )

  return (
    <div
      className="rounded-lg shadow-sm px-3 py-2 flex items-center gap-2 text-xs relative"
      style={{
        width: NODE_WIDTH,
        minHeight: NODE_HEIGHT,
        border: isGhost
          ? `1.5px dashed ${palette.borderColor}`
          : `1.5px solid ${palette.borderColor}`,
        background: palette.background,
        color: 'var(--text-primary)',
        opacity: isGhost ? 0.55 : 1,
        ...(isCurrent
          ? {
              boxShadow: `0 0 0 2px var(--brand-primary, #7851A9), 0 0 0 4px var(--bg-surface, #FBFAFD)`,
            }
          : {}),
      }}
      title={isGhost ? (node.ghostStep?.description || 'Locked step in this journey') : undefined}
    >
      <Handle id="t-top" type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle id="s-bottom" type="source" position={Position.Bottom} style={{ opacity: 0 }} />
      <Handle id="t-left" type="target" position={Position.Left} style={{ opacity: 0 }} />
      <Handle id="s-left" type="source" position={Position.Left} style={{ opacity: 0 }} />
      <Handle id="t-right" type="target" position={Position.Right} style={{ opacity: 0 }} />
      <Handle id="s-right" type="source" position={Position.Right} style={{ opacity: 0 }} />

      {isGhost && (
        <span
          className="absolute top-1 right-1"
          style={{ color: 'var(--text-secondary)' }}
          aria-label="Locked step"
        >
          <Lock className="w-3 h-3" />
        </span>
      )}

      {!isGhost && blockerCount > 0 && (
        <span
          className="absolute -top-1.5 -right-1.5 flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-app-nano font-semibold shadow-sm"
          style={{ background: 'var(--semantic-warning, #d97706)', color: '#fff' }}
          title={`${blockerCount} ${blockerCount > 1 ? 'things are' : 'thing is'} getting in the way`}
          aria-label={`${blockerCount} active blockers`}
        >
          <AlertTriangle className="w-2.5 h-2.5" />
          {blockerCount}
        </span>
      )}

      <span
        className="shrink-0"
        style={{ color: palette.borderColor, opacity: 0.85 }}
      >
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <div
          className="uppercase tracking-wide text-app-nano font-semibold"
          style={{ color: 'var(--text-secondary)' }}
        >
          {node.type.replace('_', ' ')}
        </div>
        <div
          className="truncate font-medium leading-tight"
          title={title}
          style={{ color: 'var(--text-primary)' }}
        >
          {title}
        </div>
        {showAdoptedSubtitle && pp && (
          <div
            className="truncate text-app-nano italic leading-tight"
            style={{ color: 'var(--text-secondary)', opacity: 0.8 }}
            title={`Primary owner: ${pp.title}`}
          >
            also linked to: {pp.title}
          </div>
        )}
      </div>
    </div>
  )
}

const nodeTypes: NodeTypes = { hierarchy: HierarchyNode }

// ---------------------------------------------------------------------------
// Edge styling
// ---------------------------------------------------------------------------

function styleForEdge(
  edge: GraphEdge,
): { style: Record<string, string | number>; label?: string } {
  if (edge.kind === 'structural') {
    return {
      style: {
        stroke: 'var(--brand-primary, #7851A9)',
        strokeOpacity: 0.55,
        strokeWidth: 1.5,
      },
    }
  }
  switch (edge.relationship) {
    case 'planned':
      return {
        style: {
          stroke: 'var(--brand-primary, #7851A9)',
          strokeOpacity: 0.45,
          strokeWidth: 1.4,
          strokeDasharray: '5 3',
        },
        label: 'planned',
      }
    case 'blocks':
    case 'blocked_by':
      return {
        style: {
          stroke: 'var(--semantic-error, #B75A4A)',
          strokeWidth: 1.8,
          strokeDasharray: '6 3',
        },
        label: 'blocks',
      }
    case 'derived_from':
      return {
        style: {
          stroke: 'var(--brand-lighter, #C9B8E8)',
          strokeWidth: 1.4,
          strokeDasharray: '2 4',
        },
        label: 'derived',
      }
    case 'location':
      // place -> task: where the work happens.
      return {
        style: {
          stroke: 'var(--semantic-info, #4A90A4)',
          strokeOpacity: 0.6,
          strokeWidth: 1.4,
          strokeDasharray: '1 4',
        },
        label: 'at',
      }
    default:
      return {
        style: {
          stroke: 'var(--text-secondary, #5B626C)',
          strokeOpacity: 0.7,
          strokeWidth: 1.4,
          strokeDasharray: '4 3',
        },
        label: edge.relationship,
      }
  }
}

const edgeTypes: EdgeTypes = {}

// ---------------------------------------------------------------------------
// Dagre layout
// ---------------------------------------------------------------------------

interface LayoutOptions {
  ranksep: number
  nodesep: number
  edgesep: number
}

/**
 * Plan node positions using ONLY the tree skeleton (structural edges).
 * Cross-cutting semantic links (supports / blocks / related_to / involves)
 * are overlaid later as smoothstep curves so they don't warp the hierarchy
 * or stack on top of parent→child lines.
 */
function layoutWithDagre(
  nodes: Node<GraphNodeData>[],
  structuralEdges: Edge[],
  opts: LayoutOptions,
) {
  const g = new dagre.graphlib.Graph()
  g.setDefaultEdgeLabel(() => ({}))
  g.setGraph({
    rankdir: 'TB',
    ranksep: opts.ranksep,
    nodesep: opts.nodesep,
    edgesep: opts.edgesep,
    ranker: 'network-simplex',
    marginx: 24,
    marginy: 24,
  })

  nodes.forEach((n) => {
    g.setNode(n.id, { width: NODE_WIDTH, height: NODE_HEIGHT })
  })
  structuralEdges.forEach((e) => {
    g.setEdge(e.source, e.target)
  })
  dagre.layout(g)

  return nodes.map((n) => {
    const pos = g.node(n.id)
    return {
      ...n,
      position: {
        x: (pos?.x ?? 0) - NODE_WIDTH / 2,
        y: (pos?.y ?? 0) - NODE_HEIGHT / 2,
      },
    }
  })
}

// ---------------------------------------------------------------------------
// Inner component (needs ReactFlowProvider context)
// ---------------------------------------------------------------------------

function DreamGraphInner({
  nodeId,
  nodeType,
  mode = 'branch',
  compact = false,
  showFullViewButton = false,
  fillHeight = false,
  kindFilter = null,
}: DreamGraphViewProps) {
  const navigate = useNavigate()
  const [payload, setPayload] = useState<DreamGraphPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showSemantic, setShowSemantic] = useState(true)
  const [layoutBump, setLayoutBump] = useState(0)

  useEffect(() => {
    let active = true
    setLoading(true)
    setError(null)

    const fetcher =
      mode === 'full'
        ? hierarchyGraphService.getFullLifeMap()
        : nodeId !== undefined && nodeType
          ? hierarchyGraphService.getDreamGraph(nodeType, nodeId)
          : Promise.resolve<DreamGraphPayload>({
              root: null,
              current: { id: nodeId ?? 0, type: nodeType ?? '' },
              nodes: [],
              edges: [],
            })

    fetcher
      .then((data) => {
        if (!active) return
        setPayload(data)
      })
      .catch((err: Error) => {
        if (!active) return
        setError(err.message || 'Could not load graph')
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [nodeId, nodeType, mode])

  // In-place kind filter (E12): narrow the payload BEFORE the layout pipeline
  // so everything downstream sees a smaller, still-connected graph. Matched
  // nodes keep their structural ancestors; 'world' shows people and places
  // with whatever they touch.
  const filteredPayload = useMemo(
    () => (payload ? filterPayloadByKind(payload, kindFilter) : payload),
    [payload, kindFilter],
  )

  const { nodes, edges } = useMemo(() => {
    if (!filteredPayload) {
      return { nodes: [] as Node<GraphNodeData>[], edges: [] as Edge[] }
    }

    // Deduping inverse relationships, promoting child-like semantic edges into
    // the skeleton, and synthesising locked journey steps all happen in
    // ../lifemap/graphModel — shared with the SVG diagram, because those three
    // decisions are what determine which parent a node appears under and a
    // second copy of them would eventually disagree.
    const model = buildGraphModel(filteredPayload)

    const flowNodes: Node<GraphNodeData>[] = model.nodes.map((m) => {
      const parent = m.parentKey ? model.byKey.get(m.parentKey)?.node : undefined
      return {
        id: m.key,
        type: 'hierarchy',
        position: { x: 0, y: 0 },
        data: {
          node: m.node,
          label: m.node.title,
          structuralParent: parent ? { id: parent.id, type: parent.type } : null,
        },
      }
    })

    const asGraphEdge = (
      e: (typeof model.structural)[number],
    ): GraphEdge => ({
      source: e.sourceKey,
      target: e.targetKey,
      kind: e.kind,
      relationship: e.relationship,
    })

    const structuralFlowEdges: Edge[] = model.structural.map((e) => {
      const { style, label } = styleForEdge(asGraphEdge(e))
      return {
        id: e.id,
        source: e.sourceKey,
        target: e.targetKey,
        sourceHandle: 's-bottom',
        targetHandle: 't-top',
        type: 'default',
        style,
        label: e.relationship === 'planned' ? undefined : label,
        labelStyle: { fontSize: 10, fill: 'var(--text-secondary)' },
        labelBgStyle: { fill: 'var(--bg-surface)', fillOpacity: 0.9 },
        labelBgPadding: [4, 2] as [number, number],
        labelBgBorderRadius: 4,
        markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14 },
      }
    })

    // Spread parallel semantic edges across side handles.
    const sideAssignments = new Map<string, number>()
    const sidePlanOrder: Array<['Right', 'Left'] | ['Left', 'Right']> = [
      ['Right', 'Left'],
      ['Left', 'Right'],
    ]

    const crossFlowEdges: Edge[] = showSemantic
      ? model.cross.map((e) => {
          const { style, label } = styleForEdge(asGraphEdge(e))
          const pairK = `${e.sourceKey}|${e.targetKey}`
          const occurrence = sideAssignments.get(pairK) ?? 0
          sideAssignments.set(pairK, occurrence + 1)
          const [srcSide, tgtSide] = sidePlanOrder[occurrence % sidePlanOrder.length]
          return {
            id: e.id,
            source: e.sourceKey,
            target: e.targetKey,
            sourceHandle: srcSide === 'Right' ? 's-right' : 's-left',
            targetHandle: tgtSide === 'Right' ? 't-right' : 't-left',
            type: 'smoothstep',
            data: { pathOptions: { borderRadius: 16, offset: 16 + (occurrence % 3) * 6 } },
            style,
            label,
            labelStyle: { fontSize: 10, fill: 'var(--text-secondary)' },
            labelBgStyle: { fill: 'var(--bg-surface)', fillOpacity: 0.9 },
            labelBgPadding: [4, 2] as [number, number],
            labelBgBorderRadius: 4,
            markerEnd: { type: MarkerType.ArrowClosed, width: 12, height: 12 },
            zIndex: 0,
          }
        })
      : []

    const opts: LayoutOptions =
      mode === 'full'
        ? { ranksep: 130, nodesep: 70, edgesep: 28 }
        : { ranksep: 90, nodesep: 48, edgesep: 20 }

    return {
      nodes: layoutWithDagre(flowNodes, structuralFlowEdges, opts),
      edges: [...structuralFlowEdges, ...crossFlowEdges],
    }
  }, [filteredPayload, showSemantic, mode, layoutBump])

  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: Node<GraphNodeData>) => {
      const target = node.data.node
      if (target.isGhost) return
      if (target.id === nodeId && target.type === nodeType) return
      const route = TYPE_ROUTES[target.type]
      if (!route) return
      // Places have a list, not a page of their own (2026-09-12).
      navigate(target.type === 'place' ? route : `${route}/${target.id}`)
    },
    [navigate, nodeId, nodeType],
  )

  const navigateToStep = useCallback(
    (entityType: string, entityId: number) => {
      const route = TYPE_ROUTES[entityType]
      if (!route) return
      navigate(entityType === 'place' ? route : `${route}/${entityId}`)
    },
    [navigate],
  )

  const journeyPanels = useMemo(() => {
    if (!payload?.journeys || payload.journeys.length === 0) return []
    const titlesByGoalId = new Map<number, string>()
    for (const n of payload.nodes) {
      if (n.type === 'goal') titlesByGoalId.set(n.id, n.title)
    }
    return payload.journeys
      .filter((j) => j.steps.length > 0)
      .map((j) => ({
        journey: j,
        goalTitle: titlesByGoalId.get(j.goal_id) ?? `Goal #${j.goal_id}`,
      }))
  }, [payload])

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2 text-sm text-secondary">
          <Network className="w-4 h-4 animate-pulse" />
          Loading life-map graph…
        </div>
      </Card>
    )
  }
  if (error) {
    return (
      <Card className="p-6">
        <div className="text-sm text-secondary">Could not load graph: {error}</div>
      </Card>
    )
  }
  if (!payload || nodes.length === 0) {
    return (
      <Card className="p-6">
        <div className="text-sm text-secondary">
          {mode === 'full'
            ? 'Your life map is empty. Start by creating a dream.'
            : 'No hierarchy graph yet for this item.'}
        </div>
      </Card>
    )
  }

  const canvasHeight = fillHeight
    ? '100%'
    : compact
      ? 'clamp(260px, 40vh, 380px)'
      : 'clamp(320px, 55vh, 560px)'

  return (
    /* fillHeight: percentage heights proved unreliable here (h-full on the
       Card resolved to its content height even against a definite parent —
       life-map blank-canvas bug, 2026-08-25). Flex distribution is what
       actually propagates: the page wrapper is a flex column, this Card is
       the growing item, and the canvas div grows inside it. */
    <Card className={`p-0 overflow-hidden ${fillHeight ? 'flex-1 min-h-0 flex flex-col' : ''}`}>
      {journeyPanels.length > 0 && (
        <div className="px-4 py-3 border-b border-border/50 flex flex-col gap-3">
          {journeyPanels.map(({ journey, goalTitle }) => (
            <JourneyRail
              key={`rail-${journey.plan_id ?? journey.goal_id}`}
              journey={journey}
              goalTitle={goalTitle}
              onNavigate={navigateToStep}
              showGoalTitle={mode === 'full' || journeyPanels.length > 1}
            />
          ))}
        </div>
      )}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border/50">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Network className="w-4 h-4" style={{ color: 'var(--brand-primary)' }} />
          {mode === 'full' ? 'Full life map' : 'Life-map graph'}
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-secondary">
            {payload.nodes.length} nodes · {edges.length} edges
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowSemantic((v) => !v)}
            title={showSemantic ? 'Hide cross-links' : 'Show cross-links'}
          >
            {showSemantic ? (
              <EyeOff className="w-3.5 h-3.5 mr-1.5" />
            ) : (
              <Eye className="w-3.5 h-3.5 mr-1.5" />
            )}
            {showSemantic ? 'Hide links' : 'Show links'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setLayoutBump((x) => x + 1)}
            title="Re-arrange layout"
          >
            <Network className="w-3.5 h-3.5 mr-1.5" />
            Re-arrange
          </Button>
          {showFullViewButton && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate('/life-map')}
            >
              <Maximize2 className="w-3.5 h-3.5 mr-1.5" />
              Open full view
            </Button>
          )}
        </div>
      </div>
      <div className={`w-full ${fillHeight ? 'flex-1 min-h-0' : ''}`} style={fillHeight ? undefined : { height: canvasHeight }}>
        <ReactFlow
          key={`${mode}-${layoutBump}-${showSemantic ? 's' : 'h'}`}
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodeClick={handleNodeClick}
          fitView
          fitViewOptions={{ padding: 0.18 }}
          proOptions={{ hideAttribution: true }}
          nodesDraggable
          nodesConnectable={false}
          panOnScroll
          zoomOnScroll
          minZoom={0.2}
        >
          <Background gap={18} size={1} color="var(--border-soft)" />
          <Controls showInteractive={false} position="bottom-left" />
          <MiniMap
            position="bottom-right"
            pannable
            zoomable
            nodeStrokeWidth={2}
            nodeColor={(n) => {
              const data = n.data as GraphNodeData | undefined
              if (data?.node.is_current) return 'var(--brand-primary)'
              return 'var(--brand-lighter)'
            }}
            maskColor="color-mix(in srgb, var(--bg-surface) 60%, transparent)"
          />
        </ReactFlow>
      </div>
    </Card>
  )
}

export default function DreamGraphView(props: DreamGraphViewProps) {
  return (
    <ReactFlowProvider>
      <DreamGraphInner {...props} />
    </ReactFlowProvider>
  )
}
